import React, {
  useState, useRef, useCallback, useEffect,
} from 'react';
import { Radio, Textarea } from '@/pages/common';
import * as Styles from './Survey.styles';
import SnappyHero from '@/media/logo/snappyAttentant/snappy-attendant.svg';
import { preChatActions } from '@/redux/modules';
import { useDispatch } from 'react-redux';
import { billingAnalytics } from '@/analytics';
import { IconLoading } from 'gatorcomponents';

const Survey = ({
  onClickSupport,
  enabledCancelWithSupport,
  cancelWithSupportTitle,
  cancelWithSupportSubTitle,
  cancelWithSupportTalk,
  cancelWithSupportButton,
  onOptionSelected,
  onReasonChange,
  optionId = null,
  questionDescriptionLabel = 'Your feedback is very important to us.',
  questionId = null,
  questions = [{
    id: 1,
    value: 'this is a test question!',
    options: [{
      id: 2,
      value: 'this is a test option!',
      selected: true,
    },
    {
      id: 3,
      value: 'this is a test option!',
      selected: true,
    }],
  }],
  reason = '',
  reasonQuestionLabel = 'Could you tell us a little more about the selected reason?',
  testId = 'survey',
}) => {
  const reasonRef = useRef();
  const [surveyQuestionId, setSurveyQuestionId] = useState(questionId);
  const [surveyOptionId, setSurveyOptionId] = useState(optionId);
  const [enableButtonSupport, setEnableButtonSupport] = useState(false);
  const [reasonValue, setReasonValue] = useState(reason);
  const [buttonCancelLoading, setButtonCancelLoading] = useState(false);
  const dispatch = useDispatch();
  const { subscriptions: subscriptionAnalytics } = billingAnalytics;

  const onControlOptions = (currentQuestionId, currentOptionId) => {
    const selected = surveyOptionId === currentOptionId;
    const question = questions.find(question => question.id === currentQuestionId);

    question && question.options.forEach((option) => {
      const currentOption = option;
      if (option.id !== currentOptionId) {
        currentOption.selected = !option.selected;
      }
    });

    !selected && reasonRef.current.focus();

    const questionIdValue = selected ? null : currentQuestionId;
    const optionIdValue = selected ? null : currentOptionId;
    const optionLabelValue = selected ? '' : question.options.find(option => option.selected).value;

    const subscriptionInfoBySupport = JSON.parse(localStorage.getItem('subscriptionInfoBySupport')) || {};
    subscriptionInfoBySupport.reasonSupport = optionLabelValue;
    localStorage.setItem('subscriptionInfoBySupport', JSON.stringify(subscriptionInfoBySupport));

    setReasonValue('');
    setEnableButtonSupport(true);
    setSurveyQuestionId(questionIdValue);
    setSurveyOptionId(optionIdValue);

    onOptionSelected && onOptionSelected({
      questionId: questionIdValue,
      optionId: optionIdValue,
      questionValue: optionLabelValue,
      ...(selected && { reason: '' }),
    });
  };

  const onOptionClick = (currentQuestionId, currentOptionId) => {
    onControlOptions(currentQuestionId, currentOptionId);
  };

  const handleOpenChat = useCallback(() => {
    setButtonCancelLoading(true);
    dispatch(preChatActions.chat.setActiveChat(true));
    subscriptionAnalytics.cancelSubscriptionWithSupportClick('cancelamento_imediato_com_especialista');
    setTimeout(() => {
      onClickSupport();
      setButtonCancelLoading(false);
    }, 1500);
  }, [dispatch, onClickSupport, subscriptionAnalytics]);

  const verifyVewButtonCancelWithSupport = useCallback(() => {
    if (enabledCancelWithSupport && enableButtonSupport) {
      subscriptionAnalytics.cancelSubscriptionWithSupportButtonView('cancelamento_imediato_com_especialista_botão_visualizado');
    }
  }, [enabledCancelWithSupport, enableButtonSupport, subscriptionAnalytics]);

  useEffect(() => {
    verifyVewButtonCancelWithSupport();
  }, [verifyVewButtonCancelWithSupport]);

  const handleOnReasonChange = (event) => {
    setReasonValue(event.target.value);
    onReasonChange && onReasonChange({
      questionId: surveyQuestionId,
      optionId: surveyOptionId,
      reason: event.target.value,
    });
  };

  return (
    <Styles.Wrapper data-testid={testId}>
      {questions.map(question => (
        <Styles.Question key={question.id} data-testid={`${testId}-question-${question.id}`}>
          <Styles.QuestionTitle>{ question.value }</Styles.QuestionTitle>
          <Styles.QuestionDescription>{questionDescriptionLabel}</Styles.QuestionDescription>
          <Styles.Options>
            {question.options.map(option => (
              <Styles.Option
                key={option.id}
                onClick={() => onOptionClick(question.id, option.id)}
                selected={surveyOptionId === option.id}
                visible={option.selected}
                data-testid={`${testId}-option-${option.id}`}
              >
                <Styles.OptionContent>
                  <Styles.OptionButton>
                    <Radio
                      value={option.value}
                      checked={surveyOptionId === option.id}
                      testId={`${testId}-option-${option.id}-radio`}
                    />
                  </Styles.OptionButton>
                  <Styles.OptionText
                    data-testid={`${testId}-option-${option.id}-text`}
                  >
                    {option.value}
                  </Styles.OptionText>
                </Styles.OptionContent>
              </Styles.Option>
            ))}
          </Styles.Options>

          <Styles.Reason visible={surveyOptionId !== null && !enabledCancelWithSupport} data-testid={`${testId}-reason`}>
            <Styles.ReasonQuestion>
              { reasonQuestionLabel }
            </Styles.ReasonQuestion>
            <Styles.ReasonOption>
              <Textarea
                forwardRef={reasonRef}
                onChange={handleOnReasonChange}
                placeholder="Achei que a ferramenta já vinha configurada e como não tenho experiência tive dificuldades e por isso estou cancelando."
                value={reasonValue}
                testId={`${testId}-reason-textarea`}
              />
            </Styles.ReasonOption>
          </Styles.Reason>
          {enabledCancelWithSupport && enableButtonSupport && (
            <Styles.ButtonSupport>
              <Styles.IconContainer>
                <img src={SnappyHero} alt="Ícone" />
              </Styles.IconContainer>
              <Styles.ContentContainer>
                <Styles.Title>{cancelWithSupportTitle}</Styles.Title>
                <Styles.Subtitle>{cancelWithSupportSubTitle}</Styles.Subtitle>
                <Styles.Subtitle>{cancelWithSupportTalk}</Styles.Subtitle>
                <Styles.SupportButton onClick={handleOpenChat}>
                  {buttonCancelLoading ? <IconLoading size="md" /> : cancelWithSupportButton}
                </Styles.SupportButton>
              </Styles.ContentContainer>
            </Styles.ButtonSupport>
          )}
        </Styles.Question>
      ))}
    </Styles.Wrapper>
  );
};

export default Survey;
